/*
 * Copyright 2026 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 */
package studio.data.tax

import studio.ProvinceCode
import studio.TaxBracket
import studio.TaxTable

/**
 * 2025 federal + provincial / territorial tax brackets.
 *
 * **IMPORTANT:** Each table carries a [TaxTable.verified] flag. Tables marked
 * `verified = false` must be hand-verified against the source before launch.
 * The plan in §11.1 of the spec gates the engine on these flags.
 *
 * Federal + ON + QC are the primary tested jurisdictions for this PR;
 * remaining provinces use plausible 2025 brackets but need a human pass.
 *
 * Indexation for 2026 has not yet been applied — the §11.1 freshness check
 * will surface this once we cross into 2026 reporting season (Nov 1).
 */
object Brackets2025 {

    val FEDERAL = TaxTable(
        taxYear = 2025,
        jurisdiction = "FED",
        brackets = listOf(
            TaxBracket(0.0, 57_375.0, 0.15),
            TaxBracket(57_375.0, 114_750.0, 0.205),
            TaxBracket(114_750.0, 177_882.0, 0.26),
            TaxBracket(177_882.0, 253_414.0, 0.29),
            TaxBracket(253_414.0, null, 0.33),
        ),
        sourceUrl = "https://www.canada.ca/en/revenue-agency/services/tax/individuals/frequently-asked-questions-individuals/canadian-income-tax-rates-individuals-current-previous-years.html",
        effectiveDate = "2025-01-01",
        verified = true,
    )

    val ONTARIO = TaxTable(
        taxYear = 2025,
        jurisdiction = "ON",
        brackets = listOf(
            TaxBracket(0.0, 52_886.0, 0.0505),
            TaxBracket(52_886.0, 105_775.0, 0.0915),
            TaxBracket(105_775.0, 150_000.0, 0.1116),
            TaxBracket(150_000.0, 220_000.0, 0.1216),
            TaxBracket(220_000.0, null, 0.1316),
        ),
        sourceUrl = "https://www.fin.gov.on.ca/en/tax/pit/rates.html",
        effectiveDate = "2025-01-01",
        verified = true,
    )

    /**
     * Ontario surtaxes — applied on top of provincial tax, not income directly.
     * Triggers at 2025 provincial-tax thresholds of $5,710 (20%) and $7,307 (36%),
     * which on a marginal-income basis kick in around taxable income of ~$90K and
     * ~$109K respectively. The [MarginalRateCalculator] folds these in.
     */
    val ONTARIO_SURTAX_THRESHOLD_20PCT = 5_710.0
    val ONTARIO_SURTAX_THRESHOLD_36PCT = 7_307.0
    val ONTARIO_SURTAX_RATE_LOW = 0.20
    val ONTARIO_SURTAX_RATE_HIGH = 0.36

    val QUEBEC = TaxTable(
        taxYear = 2025,
        jurisdiction = "QC",
        brackets = listOf(
            TaxBracket(0.0, 53_255.0, 0.14),
            TaxBracket(53_255.0, 106_495.0, 0.19),
            TaxBracket(106_495.0, 129_590.0, 0.24),
            TaxBracket(129_590.0, null, 0.2575),
        ),
        sourceUrl = "https://www.revenuquebec.ca/en/citizens/your-situation/new-residents/the-quebec-tax-system/income-tax-rates/",
        effectiveDate = "2025-01-01",
        verified = true,
    )

    val BRITISH_COLUMBIA = TaxTable(
        taxYear = 2025,
        jurisdiction = "BC",
        brackets = listOf(
            TaxBracket(0.0, 49_279.0, 0.0506),
            TaxBracket(49_279.0, 98_560.0, 0.077),
            TaxBracket(98_560.0, 113_158.0, 0.105),
            TaxBracket(113_158.0, 137_407.0, 0.1229),
            TaxBracket(137_407.0, 186_306.0, 0.147),
            TaxBracket(186_306.0, 259_829.0, 0.168),
            TaxBracket(259_829.0, null, 0.205),
        ),
        sourceUrl = "https://www2.gov.bc.ca/gov/content/taxes/income-taxes/personal",
        effectiveDate = "2025-01-01",
        verified = false,
    )

    val ALBERTA = TaxTable(
        taxYear = 2025,
        jurisdiction = "AB",
        brackets = listOf(
            TaxBracket(0.0, 151_234.0, 0.10),
            TaxBracket(151_234.0, 181_481.0, 0.12),
            TaxBracket(181_481.0, 241_974.0, 0.13),
            TaxBracket(241_974.0, 362_961.0, 0.14),
            TaxBracket(362_961.0, null, 0.15),
        ),
        sourceUrl = "https://www.alberta.ca/personal-income-tax",
        effectiveDate = "2025-01-01",
        verified = false,
    )

    val MANITOBA = TaxTable(
        taxYear = 2025,
        jurisdiction = "MB",
        brackets = listOf(
            TaxBracket(0.0, 47_000.0, 0.108),
            TaxBracket(47_000.0, 100_000.0, 0.1275),
            TaxBracket(100_000.0, null, 0.174),
        ),
        sourceUrl = "https://www.gov.mb.ca/finance/personal/pcredits.html",
        effectiveDate = "2025-01-01",
        verified = false,
    )

    val SASKATCHEWAN = TaxTable(
        taxYear = 2025,
        jurisdiction = "SK",
        brackets = listOf(
            TaxBracket(0.0, 53_463.0, 0.105),
            TaxBracket(53_463.0, 152_750.0, 0.125),
            TaxBracket(152_750.0, null, 0.145),
        ),
        sourceUrl = "https://www.saskatchewan.ca/residents/taxes-and-investments/income-tax",
        effectiveDate = "2025-01-01",
        verified = false,
    )

    val NOVA_SCOTIA = TaxTable(
        taxYear = 2025,
        jurisdiction = "NS",
        brackets = listOf(
            TaxBracket(0.0, 29_590.0, 0.0879),
            TaxBracket(29_590.0, 59_180.0, 0.1495),
            TaxBracket(59_180.0, 93_000.0, 0.1667),
            TaxBracket(93_000.0, 150_000.0, 0.175),
            TaxBracket(150_000.0, null, 0.21),
        ),
        sourceUrl = "https://novascotia.ca/finance/en/home/taxation/tax101/personalincometax/default.aspx",
        effectiveDate = "2025-01-01",
        verified = false,
    )

    val NEW_BRUNSWICK = TaxTable(
        taxYear = 2025,
        jurisdiction = "NB",
        brackets = listOf(
            TaxBracket(0.0, 51_306.0, 0.094),
            TaxBracket(51_306.0, 102_614.0, 0.14),
            TaxBracket(102_614.0, 190_060.0, 0.16),
            TaxBracket(190_060.0, null, 0.195),
        ),
        sourceUrl = "https://www2.gnb.ca/content/gnb/en/departments/finance/taxes.html",
        effectiveDate = "2025-01-01",
        verified = false,
    )

    val PRINCE_EDWARD_ISLAND = TaxTable(
        taxYear = 2025,
        jurisdiction = "PE",
        brackets = listOf(
            TaxBracket(0.0, 33_328.0, 0.095),
            TaxBracket(33_328.0, 64_656.0, 0.1347),
            TaxBracket(64_656.0, 105_000.0, 0.166),
            TaxBracket(105_000.0, 140_000.0, 0.1762),
            TaxBracket(140_000.0, null, 0.19),
        ),
        sourceUrl = "https://www.princeedwardisland.ca/en/topic/income-tax",
        effectiveDate = "2025-01-01",
        verified = false,
    )

    val NEWFOUNDLAND_AND_LABRADOR = TaxTable(
        taxYear = 2025,
        jurisdiction = "NL",
        brackets = listOf(
            TaxBracket(0.0, 44_192.0, 0.087),
            TaxBracket(44_192.0, 88_382.0, 0.145),
            TaxBracket(88_382.0, 157_792.0, 0.158),
            TaxBracket(157_792.0, 220_910.0, 0.178),
            TaxBracket(220_910.0, 282_214.0, 0.198),
            TaxBracket(282_214.0, 564_429.0, 0.208),
            TaxBracket(564_429.0, 1_128_858.0, 0.213),
            TaxBracket(1_128_858.0, null, 0.218),
        ),
        sourceUrl = "https://www.gov.nl.ca/fin/tax-programs-incentives/personal/",
        effectiveDate = "2025-01-01",
        verified = false,
    )

    val YUKON = TaxTable(
        taxYear = 2025,
        jurisdiction = "YT",
        brackets = listOf(
            TaxBracket(0.0, 57_375.0, 0.064),
            TaxBracket(57_375.0, 114_750.0, 0.09),
            TaxBracket(114_750.0, 177_882.0, 0.109),
            TaxBracket(177_882.0, 500_000.0, 0.128),
            TaxBracket(500_000.0, null, 0.15),
        ),
        sourceUrl = "https://yukon.ca/en/taxes",
        effectiveDate = "2025-01-01",
        verified = false,
    )

    val NORTHWEST_TERRITORIES = TaxTable(
        taxYear = 2025,
        jurisdiction = "NT",
        brackets = listOf(
            TaxBracket(0.0, 51_964.0, 0.059),
            TaxBracket(51_964.0, 103_930.0, 0.086),
            TaxBracket(103_930.0, 168_967.0, 0.122),
            TaxBracket(168_967.0, null, 0.1405),
        ),
        sourceUrl = "https://www.fin.gov.nt.ca/en/services/income-tax",
        effectiveDate = "2025-01-01",
        verified = false,
    )

    val NUNAVUT = TaxTable(
        taxYear = 2025,
        jurisdiction = "NU",
        brackets = listOf(
            TaxBracket(0.0, 54_707.0, 0.04),
            TaxBracket(54_707.0, 109_413.0, 0.07),
            TaxBracket(109_413.0, 177_881.0, 0.09),
            TaxBracket(177_881.0, null, 0.115),
        ),
        sourceUrl = "https://gov.nu.ca/finance/information/personal-income-tax",
        effectiveDate = "2025-01-01",
        verified = false,
    )

    fun provincial(province: ProvinceCode): TaxTable = when (province) {
        ProvinceCode.AB -> ALBERTA
        ProvinceCode.BC -> BRITISH_COLUMBIA
        ProvinceCode.MB -> MANITOBA
        ProvinceCode.NB -> NEW_BRUNSWICK
        ProvinceCode.NL -> NEWFOUNDLAND_AND_LABRADOR
        ProvinceCode.NS -> NOVA_SCOTIA
        ProvinceCode.NT -> NORTHWEST_TERRITORIES
        ProvinceCode.NU -> NUNAVUT
        ProvinceCode.ON -> ONTARIO
        ProvinceCode.PE -> PRINCE_EDWARD_ISLAND
        ProvinceCode.QC -> QUEBEC
        ProvinceCode.SK -> SASKATCHEWAN
        ProvinceCode.YT -> YUKON
    }
}
